import { useState } from 'react';
import type { RadioStation } from '@/mocks/radios';
import { getStationPosts, getStationPhotos, getStationVideos, type RadioPost } from '@/mocks/radioContent';

interface RadioFeedSectionProps {
  station: RadioStation;
}

type FeedTab = 'todo' | 'fotos' | 'videos' | 'concursos';

export default function RadioFeedSection({ station }: RadioFeedSectionProps) {
  const [activeTab, setActiveTab] = useState<FeedTab>('todo');
  const posts = getStationPosts(station.id);
  const photos = getStationPhotos(station.id);
  const videos = getStationVideos(station.id);

  const filteredPosts = activeTab === 'todo'
    ? posts
    : activeTab === 'concursos'
      ? posts.filter((p) => p.type === 'concurso')
      : posts;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Hoy';
    if (days === 1) return 'Ayer';
    if (days < 7) return `Hace ${days} días`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Feed Social</h3>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5">
          {([
            { key: 'todo' as FeedTab, label: 'Todo', icon: 'ri-apps-line' },
            { key: 'fotos' as FeedTab, label: 'Fotos', icon: 'ri-image-line' },
            { key: 'videos' as FeedTab, label: 'Videos', icon: 'ri-video-line' },
            { key: 'concursos' as FeedTab, label: 'Concursos', icon: 'ri-gift-line' },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content based on tab */}
      <div>
        {activeTab === 'fotos' && (
          <div className="p-4 grid grid-cols-3 md:grid-cols-4 gap-2">
            {photos.slice(0, 8).map((photo) => (
              <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-background-200/70 group cursor-pointer relative">
                <img
                  src={photo.url}
                  alt={photo.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end p-2">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs font-medium line-clamp-2">{photo.caption}</p>
                    <div className="flex items-center gap-2 text-white/80 text-[10px] mt-1">
                      <span><i className="ri-heart-fill"></i> {photo.likes}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {photos.length > 8 && (
              <button className="aspect-square rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200/70 transition-colors">
                <span className="text-sm font-semibold text-foreground-600">+{photos.length - 8} más</span>
              </button>
            )}
          </div>
        )}

        {activeTab === 'videos' && (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {videos.slice(0, 4).map((video) => (
              <div key={video.id} className="rounded-lg overflow-hidden bg-background-100 border border-background-200/70 group cursor-pointer">
                <div className="relative aspect-video bg-background-200/70 overflow-hidden">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-xl text-foreground-950 ml-0.5"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-[10px] font-medium">
                    {video.duration}
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-foreground-950 line-clamp-1">{video.title}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{formatNumber(video.views)} vistas · {formatDate(video.date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {(activeTab === 'todo' || activeTab === 'concursos') && (
          <div className="divide-y divide-background-200/70">
            {filteredPosts.slice(0, activeTab === 'todo' ? 6 : 4).map((post) => (
              <PostCard key={post.id} post={post} formatDate={formatDate} formatNumber={formatNumber} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-background-200/70 flex justify-center">
        <button className="text-sm font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap">
          Ver más publicaciones
          <i className="ri-arrow-down-s-line"></i>
        </button>
      </div>
    </section>
  );
}

function PostCard({ post, formatDate, formatNumber }: { post: RadioPost; formatDate: (d: string) => string; formatNumber: (n: number) => string }) {
  const [liked, setLiked] = useState(post.liked);
  const [likesCount, setLikesCount] = useState(post.likes);
  const [commentsCount, setCommentsCount] = useState(post.comments);
  const [sharesCount, setSharesCount] = useState(post.shares);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');

  const handleLike = () => {
    if (liked) {
      setLiked(false);
      setLikesCount(likesCount - 1);
    } else {
      setLiked(true);
      setLikesCount(likesCount + 1);
    }
  };

  const handleCommentSubmit = () => {
    if (commentText.trim()) {
      setCommentsCount(commentsCount + 1);
      setCommentText('');
      setShowCommentInput(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/radio/${post.stationId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.text.slice(0, 60), text: post.text.slice(0, 100), url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
    }
    setSharesCount(sharesCount + 1);
  };

  const typeLabels: Record<string, { label: string; icon: string; color: string }> = {
    texto: { label: 'Publicación', icon: 'ri-file-text-line', color: 'bg-foreground-100 text-foreground-700' },
    imagen: { label: 'Foto', icon: 'ri-image-line', color: 'bg-secondary-100 text-secondary-700' },
    video: { label: 'Video', icon: 'ri-video-line', color: 'bg-accent-100 text-accent-700' },
    evento: { label: 'Evento', icon: 'ri-calendar-event-line', color: 'bg-primary-100 text-primary-700' },
    live: { label: 'En Vivo', icon: 'ri-live-line', color: 'bg-red-100 text-red-700' },
    concurso: { label: 'Concurso', icon: 'ri-gift-line', color: 'bg-amber-100 text-amber-700' },
    anuncio: { label: 'Anuncio', icon: 'ri-megaphone-line', color: 'bg-emerald-100 text-emerald-700' },
  };

  const typeInfo = typeLabels[post.type] || typeLabels.texto;

  return (
    <div className="px-5 py-4 hover:bg-background-100/50 transition-colors">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${typeInfo.color}`}>
          <i className={`${typeInfo.icon} text-xs`}></i>
          {typeInfo.label}
        </div>
        {post.featured && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold whitespace-nowrap">
            <i className="ri-pushpin-fill text-xs"></i>
            Destacado
          </div>
        )}
        <span className="text-xs text-foreground-400 ml-auto whitespace-nowrap">{formatDate(post.date)}</span>
      </div>

      {/* Text */}
      <p className="text-sm text-foreground-800 mb-3 leading-relaxed">{post.text}</p>

      {/* Media */}
      {(post.imageUrl || post.videoUrl) && (
        <div className="mb-3 rounded-lg overflow-hidden bg-background-200/70">
          {post.imageUrl && (
            <img
              src={post.imageUrl}
              alt=""
              className="w-full max-h-[400px] object-cover cursor-pointer"
            />
          )}
          {post.videoUrl && (
            <div className="relative aspect-video cursor-pointer group">
              <img
                src={post.videoUrl}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-play-fill text-2xl text-foreground-950 ml-0.5"></i>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event Info */}
      {post.eventDate && (
        <div className="mb-3 p-3 rounded-lg bg-background-100 border border-background-200/70">
          <div className="flex items-center gap-2 text-sm text-foreground-700">
            <i className="ri-calendar-line text-base text-primary-500"></i>
            <span className="font-semibold">{post.eventDate}</span>
            {post.eventTime && <span className="text-foreground-500">· {post.eventTime}</span>}
          </div>
          {post.eventCity && (
            <div className="flex items-center gap-2 text-sm text-foreground-500 mt-1">
              <i className="ri-map-pin-line text-base"></i>
              <span>{post.eventCity}</span>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4 text-sm">
        <button onClick={handleLike} className={`flex items-center gap-1.5 cursor-pointer transition-colors whitespace-nowrap ${liked ? 'text-red-500' : 'text-foreground-500 hover:text-red-500'}`}>
          <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
          <span className="font-medium">{formatNumber(likesCount)}</span>
        </button>
        <button onClick={() => setShowCommentInput(!showCommentInput)} className="flex items-center gap-1.5 text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap">
          <i className="ri-chat-3-line text-base"></i>
          <span className="font-medium">{formatNumber(commentsCount)}</span>
        </button>
        <button onClick={handleShare} className="flex items-center gap-1.5 text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap">
          <i className="ri-share-forward-line text-base"></i>
          <span className="font-medium">{formatNumber(sharesCount)}</span>
        </button>
      </div>

      {/* Inline comment input */}
      {showCommentInput && (
        <div className="mt-3 flex items-center gap-2">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCommentSubmit(); }}
            placeholder="Escribí un comentario..."
            className="flex-1 px-3 py-2 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-secondary-300 text-foreground-800"
            autoFocus
          />
          <button
            onClick={handleCommentSubmit}
            disabled={!commentText.trim()}
            className="px-3 py-2 text-xs font-semibold bg-secondary-500 text-background-50 rounded-lg hover:bg-secondary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
          >
            Enviar
          </button>
        </div>
      )}
    </div>
  );
}